import { forwardRef, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useLanguages } from "lang/languages"
import { useDialogs, useLoadingIndicator } from "modules/overlays"
import { useSite } from "modules/sites"
import { useAuth, useUser } from "modules/users"

import Button from "components/Button"
import SelectField from "components/Form/SelectField"
import Icon from "components/Icon"
import Panel from "components/Modality/Panel"

import styles from "./UserPanel.module.scss"

const UserPanel = ({ className }, ref) => {
  const { t } = useTranslation("user")
  const site = useSite()
  const { languages, switchLanguage } = useLanguages()
  const user = useUser()
  const { logOut } = useAuth()
  const withLoadingIndicator = useLoadingIndicator()
  const { openErrorModal } = useDialogs()

  const userLogOut = useCallback(() => {
    withLoadingIndicator(logOut())
      .then(() => window.location.assign("/"))
      .catch((error) =>
        openErrorModal({
          message: error.message,
          title: t("authentication:error_logout"),
        }),
      )
  }, [withLoadingIndicator, logOut, openErrorModal, t])

  const roles = user.Roles || user.roles

  return (
    <Panel ref={ref} className={className} title={user.UserDisplayName || user.employeeName}>
      <ul className={styles.list}>
        <li>{user.DealerName || user.companyName}</li>
        <li>{user.UserDisplayCName || user.employeeCName}</li>
        <li>{user.Email || user.email}</li>
        <li>{user.UserId || user.employeeCode}</li>
        {roles.map((role) => (
          <li key={role}>{role}</li>
        ))}
      </ul>
      {site.name !== "sales" ? (
        <fieldset className={styles.languages}>
          <legend>{t("field_language")}</legend>
          <SelectField defaultValue={languages[0].code} onChange={switchLanguage}>
            {languages.map((language) => (
              <option key={language.code} value={language.code}>
                {language.name}
              </option>
            ))}
          </SelectField>
        </fieldset>
      ) : null}
      <hr className={styles.line} />
      <Button className={styles.logout} variant="bare" onClick={userLogOut}>
        <Icon className={styles.icon} icon="log-out" />
        <span>{t("label:logout")}</span>
      </Button>
    </Panel>
  )
}

export default forwardRef(UserPanel)
